import { type CredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { type ChangeEvent, useCallback, useContext, useState } from 'react';

import HOSTS from '@/constants/hosts';
import PendingAccess from '@/pages/PendingAccess/PendingAccess';
import Heading from '@/ui/components/Heading';
import { setLocalStorageSession } from '@/utils/authUtils';
import isInChromeExtension from '@/utils/isInChromeExtension';

import AppContext from '../../contexts/appContext';
import { authRepository } from '../../repositories/AuthRepository';

function Login() {
  const { host, setCurrentUser, setHost } = useContext(AppContext);
  const [pendingAccess, setPendingAccess] = useState(false);

  const inIframe = window !== window.top;
  const fedcmSupported = 'IdentityCredential' in window;

  // Synchronously detect Chrome extension iframe context via ancestorOrigins
  // (Chromium-only API). When Dugout is loaded inside the extension sidepanel,
  // the immediate parent origin starts with "chrome-extension://". Checking this
  // synchronously avoids the race where GIS starts FedCM in the sidepanel, which causes the
  // "FedCM get() AbortError: signal is aborted without reason".
  const [isExtensionSidepanel] = useState(isInChromeExtension);

  const googleLoginProps = isExtensionSidepanel
    ? {
        use_fedcm_for_button: false,
        ux_mode: 'popup' as const
      }
    : {
        use_fedcm_for_button: fedcmSupported && !inIframe
      };

  const onGoogleLoginSuccess = useCallback(
    (response: CredentialResponse) => {
      if (response.credential) {
        authRepository
          .getToken({ host, token: response.credential })
          .then((staffSessionResponse) => {
            const currentUser = staffSessionResponse.data;
            if (currentUser) {
              setLocalStorageSession(currentUser, host);
              setCurrentUser(currentUser);
              return;
            }

            const errorResponse = staffSessionResponse.error?.response;
            if (
              errorResponse?.status === 403 &&
              (errorResponse.data as { code?: string })?.code === 'staff_not_provisioned'
            ) {
              setPendingAccess(true);
            }
          });
      }
    },
    [host, setCurrentUser]
  );

  function updateHost(event: ChangeEvent<HTMLSelectElement>) {
    const host = event.target.value;
    setHost(host);
  }

  if (pendingAccess) {
    return <PendingAccess onDismiss={() => setPendingAccess(false)} />;
  }

  return (
    <article className="flex flex-col items-center justify-center flex-1 p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col items-center gap-6">
        <img
          src="/images/members/header-logo-light.svg"
          alt="Affect Therapeutics"
          className="h-12 w-auto dark:hidden"
        />
        <img
          src="/images/members/header-logo.svg"
          alt="Affect Therapeutics"
          className="h-12 w-auto hidden dark:block"
        />

        <div className="text-center flex flex-col gap-1">
          <Heading value={2} text="Welcome to Affect" className="text-gray-900" />
          <p className="text-sm text-gray-500">
            Sign in with your staff Google account to continue
          </p>
        </div>

        <GoogleOAuthProvider clientId="561050020393-dgf8e23d3t4tpmlj3k7volf86fldlepf.apps.googleusercontent.com">
          <GoogleLogin
            {...googleLoginProps}
            onSuccess={onGoogleLoginSuccess}
            onError={() => {
              console.log('Login Failed');
            }}
          />
        </GoogleOAuthProvider>

        <p className="text-xs text-gray-400">
          &reg; {new Date().getFullYear()} Affect Therapeutics &bull; HIPAA Compliant
        </p>

        <footer className="w-full border-t border-gray-100 pt-4 flex justify-center">
          <label className="text-xs text-gray-400 flex items-center gap-2">
            Environment:
            <select
              data-testid="host-select"
              onChange={updateHost}
              value={host}
              className="text-xs text-gray-500 border border-gray-200 rounded px-1 py-0.5">
              {Object.entries(HOSTS).map(([env, url]) => (
                <option value={url} key={env}>
                  {env}
                </option>
              ))}
            </select>
          </label>
        </footer>
      </div>
    </article>
  );
}

export default Login;
